import Vue from 'vue'
import Router from 'vue-router'
import posthog from 'posthog-js'

// Public
import Home from './views/landing/Home.vue'
import Signup from './views/landing/Signup.vue'
import SignupConfirm from './views/landing/SignupConfirm.vue'
import Login from './views/landing/Login.vue'
import ForgotPassword from './views/landing/ForgotPassword.vue'
import ResetPassword from './views/landing/ResetPassword.vue'
import EmbedAccessDenied from './views/EmbedAccessDenied.vue'

// Private
import Dashboard from './views/panel/Dashboard/Index.vue'
import GridRank from './views/panel/Agency/GridRank.vue'
import QuickScanDetails from './views/panel/Agency/QuickScanDetails.vue'
import GridScanDetails from './views/panel/Agency/GridScanDetails.vue'
import UserSettings from './views/panel/UserSettings.vue'
import Settings from './views/panel/Agency/Settings.vue'
import PaymentSuccess from './views/panel/PaymentSuccess.vue'
import Billing from './views/panel/Billing.vue'
import Scans from './views/panel/Scans.vue'
import CreateScan from './views/panel/CreateScan.vue'
import TestTokenRefresh from './views/TestTokenRefresh.vue'


// END Agency components

// Admin components
import Agencies from './views/panel/Admin/Agencies.vue'
// END Admin components

import EmailVerify from './views/panel/EmailVerify.vue'


import store from './store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: function(to) {
    if (to.hash) {
      return {selector: to.hash}
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Login
    },
    {
      path: '/signup',
      name: 'signup',
      component: Signup
    },
    {
      path: '/signup/plan/:plan_id',
      name: 'signup_plan',
      component: Signup
    },
    {
      path: '/signup/:company_type/plan/:plan_id',
      name: 'signup_company_type',
      component: Signup
    },
    {
      path: '/signup/confirm',
      name: 'signup_confirm',
      component: SignupConfirm
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta:{
        guest:true,
      }
    },
    {
      path: '/embed-access-denied',
      name: 'embed-access-denied',
      component: EmbedAccessDenied
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: ResetPassword
    },

    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: { requiresAuth: true }
    },
    {
      path: '/payment-success',
      name: 'payment-success',
      component: PaymentSuccess,
      meta: { requiresAuth: true }
    },

    {
      path: '/auth/:provider/callback',
      component: {
        template: '<div class="auth-component"></div>'
      }
    },
    {
      path: '/grid-scans',
      name: 'GridRank',
      component: GridRank,
      meta: { requiresAuth: true }
    },
        {
      path: '/quick-scan/:id',
      name: 'QuickScanDetails',
      component: QuickScanDetails,
      meta: { requiresAuth: true }
    },
    {
      path: '/grid-scan/:id',
      name: 'GridScanDetails',
      component: GridScanDetails,
      meta: { requiresAuth: true }
    },
    {
      path: '/view-grid/:id/:mode?',
      name: 'ViewGrid',
      component: () => import('@/views/panel/ViewGrid.vue'),
      meta: {
        title: 'View Grid',
        // This route requires auth only when mode is not 'share' or 'embedded'
        conditionalAuth: true,
        publicModes: ['share', 'embedded']
      }
    },
    {
      path: '/recurring-grid/:id/:mode?',
      name: 'RecurringGrid',
      component: () => import('@/views/panel/RecurringGrid.vue'),
      meta: {
        title: 'Recurring Grid',
        // This route requires auth only when mode is not 'share' or 'embedded'
        conditionalAuth: true,
        publicModes: ['share', 'embedded']
      }
    },
    {
      path: '/user-settings',
      name: 'user-settings',
      component: UserSettings,
      meta: { requiresAuth: true }
    },
    {
      path: '/billing',
      name: 'billing',
      component: Billing,
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'settings',
      component: Settings,
      meta: { requiresAuth: true }
    },
    {
      path: '/scans',
      name: 'scans',
      component: Scans,
      meta: { requiresAuth: true }
    },
    {
      path: '/test-token-refresh',
      name: 'test-token-refresh',
      component: TestTokenRefresh,
      meta: { requiresAuth: true }
    },
    {
      path: '/create-scan',
      name: 'create-scan',
      component: CreateScan,
      meta: { requiresAuth: true }
    },
    {
      path: '/scan-report/:id/:mode?',
      name: 'scan-report',
      component: () => import('@/views/panel/ScanReport.vue'),
      meta: { title: 'Scan Report' } // Removed requiresAuth to allow guest access
    },
    {
      path: '/recurring-report/:id/:mode?',
      name: 'recurring-report',
      component: () => import('@/views/panel/RecurringReport.vue'),
      meta: { title: 'Recurring Report' } // Removed requiresAuth to allow guest access
    },

  ]
})
router.beforeEach(async (to, from, next) => {
  // Check for embed mode
  const isEmbedMode = to.query.embed === 'true';
  // Prefer token from URL if provided (parent might have updated it)
  // Otherwise use stored token (from refresh), fallback to null
  const urlToken = to.query.jwt;
  const storedEmbedToken = store.getters['embed/embedToken'];
  const embedToken = urlToken || storedEmbedToken;

  if (isEmbedMode) {
    // Whitelist of allowed parent domains for embedding
    // Configure via environment variable: VUE_APP_ALLOWED_EMBED_DOMAINS
    const ALLOWED_PARENT_DOMAINS = process.env.VUE_APP_ALLOWED_EMBED_DOMAINS
      ? process.env.VUE_APP_ALLOWED_EMBED_DOMAINS.split(',').map(d => d.trim())
      : [
          'http://app.seomaven.ai',
          'https://app.seomaven.ai',
          'https://keywords.bitsmiths.dev',
          'http://localhost:8080', // For local development
          'http://localhost:3000',
          'http://127.0.0.1:8080'
        ];

    // Check if iframe is embedded on an allowed domain
    let isAllowedParent = false;
    let parentOrigin = null;

    // ===== TEMPORARY: Allow ALL domains for testing =====
    // To revert: Change 'true' back to 'false' below
    const ALLOW_ALL_DOMAINS_FOR_TESTING = true;
    // ===== END TEMPORARY =====

    if (ALLOW_ALL_DOMAINS_FOR_TESTING) {
      isAllowedParent = true;
    }

    try {
      // Method 1: Check ancestorOrigins (Chrome/Edge only, most reliable)
      if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
        parentOrigin = window.location.ancestorOrigins[0];
        if (!ALLOW_ALL_DOMAINS_FOR_TESTING) {
          isAllowedParent = ALLOWED_PARENT_DOMAINS.some(domain => parentOrigin.startsWith(domain));
        }
      }
      // Method 2: Check document.referrer as fallback
      else if (document.referrer) {
        parentOrigin = document.referrer;
        isAllowedParent = ALLOWED_PARENT_DOMAINS.some(domain => document.referrer.startsWith(domain));
      }
      // Method 3: Check if we're in an iframe at all
      else if (window !== window.top) {
        // We're in iframe but can't determine parent (cross-origin restrictions)
        // For security, we'll block this
        isAllowedParent = false;
      } else {
        // Not in iframe - direct access with embed=true
        isAllowedParent = false;
      }
    } catch (error) {
      isAllowedParent = false;
    }

    // Block access if not from allowed parent
    if (!isAllowedParent) {
      // Show error page
      next({
        name: 'embed-access-denied',
        query: {
          reason: 'unauthorized_domain',
          attempted: parentOrigin || 'unknown'
        }
      });
      return;
    }

    // Always set embed mode when detected and validated
    store.dispatch('embed/initializeEmbedMode', { token: embedToken });

    if (embedToken) {
      // Check if the token has changed from the current one
      const currentToken = store.getters['auth/jwt'] || store.getters['auth/accessToken'];

      // If token is different or no auth, update the session
      if (currentToken !== embedToken || !store.getters['auth/isAuthenticated']) {
        // Clear previous session data
        store.commit('auth/logout');
        store.dispatch('apiKeys/clearApiKeys');

        // Create a session-like object with the JWT token
        store.commit('auth/setSession', {
          access_token: embedToken,
          token_type: 'bearer'
        });

        // Decode JWT to get user info
        try {
          const base64Url = embedToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join(''));
          const tokenData = JSON.parse(jsonPayload);

          // Set user data from token
          const userMetadata = (tokenData && tokenData.user_metadata) || {};
          store.commit('auth/setUser', {
            id: tokenData.sub,
            email: tokenData.email,
            first_name: (userMetadata && userMetadata.first_name) || '',
            last_name: (userMetadata && userMetadata.last_name) || '',
            has_active_subscription: true
          });

          // Force reload API keys for the new user
          store.dispatch('apiKeys/fetchGoogleMapsKey');
        } catch (error) {
          console.error('Error decoding JWT:', error);
        }
      }
    }

    // ===== Listen for token updates from parent app =====
    window.addEventListener('message', (event) => {
      // Verify origin - must be from allowed parent
      if (!ALLOWED_PARENT_DOMAINS.some(domain => event.origin.startsWith(domain))) {
        return;
      }

      // Handle token update from parent
      if (event.data && event.data.type === 'GEEOGRID_TOKEN_UPDATE') {
        const newToken = event.data.token;

        if (newToken) {
          // console.log('🔄 Received updated token from parent');

          // Update token in localStorage (axios uses this)
          localStorage.setItem('auth_token', newToken);

          // Update embed token in store
          store.commit('embed/setEmbedToken', newToken);

          // Update auth session
          store.commit('auth/setSession', {
            access_token: newToken,
            token_type: 'bearer'
          });

          // console.log('✅ Token updated in iframe');
        }
      }
    });
    // ===== END TOKEN UPDATE LISTENER =====
  }
  // Don't check auth if navigating from signup to authenticated pages
  // This avoids race conditions after registration
  const comingFromSignup = from.name === 'signup' || from.path === '/signup'
  const goingToAuthPage = to.matched.some(record => record.meta.requiresAuth)

  // Skip auth check in embed mode as we handle it differently
  if (!isEmbedMode && (!comingFromSignup || !goingToAuthPage)) {
    // Only check auth if not coming from signup and not in embed mode
    await store.dispatch('auth/checkAuth').catch(() => {
      // Ignore errors, just means user is not authenticated
    });
  }

  const jwt = store.getters['auth/jwt'] || store.getters['auth/accessToken']
  const isLoggedIn = store.getters['auth/isAuthenticated']

  // Check for conditional auth routes
  const hasConditionalAuth = to.matched.some(record => record.meta.conditionalAuth)
  if (hasConditionalAuth) {
    const mode = to.params.mode
    const routeMeta = to.matched.find(record => record.meta.conditionalAuth).meta
    const publicModes = routeMeta.publicModes || []

    // If mode is public, allow access without auth
    if (mode && publicModes.includes(mode)) {
      next()
      return
    }

    // Otherwise, require authentication
    if (!isLoggedIn) {
      next('/login')
      return
    }
  }

  // Check if route requires auth (standard auth check)
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth) {
    if (!isLoggedIn) {
      if (to.path !== '/login') {
        next('/login')
      } else {
        next(false)
      }
    } else {
      /* ===== SUBSCRIPTION_FEATURE_START ===== */
      // Subscription check disabled - free access for all users
      // // Check subscription status for authenticated users
      // // Allow access to user-settings, login, and signup pages regardless of subscription
      // const exemptPages = ['/user-settings', '/login', '/signup', '/forgot-password', '/reset-password'];
      // const isExemptPage = exemptPages.some(page => to.path.startsWith(page));

      // // Load user data if not loaded
      // if (!store.state.me.me) {
      //   await store.dispatch('me/fetch');
      // }

      // // Check subscription status
      // const hasActiveSubscription = store.getters['me/hasActiveSubscription'];

      // if (!hasActiveSubscription && !isExemptPage) {
      //   // Redirect to user-settings with subscription tab
      //   next({
      //     path: '/user-settings',
      //     query: { tab: 'billing', subscriptionRequired: 'true' }
      //   });
      //   return;
      // }
      /* ===== SUBSCRIPTION_FEATURE_END ===== */

      next()
    }

  } else if (to.matched.some(record => record.meta.guest)) {
    if (isLoggedIn) {
      if (to.path !== '/scans') {
        next('/scans')
      } else {
        next(false)
      }
    } else {
      next()
    }

  } else {
    next()
  }

  // Load API keys after authentication is established (for both normal and embed mode)
  if (isLoggedIn) {
    // Check if the user has changed (different user ID)
    const currentUserId = store.state.auth.user && store.state.auth.user.id
    const storedUserId = store.state.apiKeys.lastUserId

    if (currentUserId && currentUserId !== storedUserId) {
      // User has changed, clear and reload API keys
      store.dispatch('apiKeys/clearApiKeys')
      store.dispatch('apiKeys/fetchGoogleMapsKey')
    } else if (!store.state.apiKeys.googleMaps.apiKey && !store.state.apiKeys.googleMaps.loading) {
      // No API key loaded, fetch it
      store.dispatch('apiKeys/fetchGoogleMapsKey')
    }
  }
})

// Track page views with PostHog
router.afterEach((to) => {
  if (posthog && to.name) {
    posthog.capture('$pageview')
  }
})

export default router